import { useState, useEffect } from 'react';
import useSubmitFeedback from '../../../../data/mutationHooks/member/physicalTherapist/useSubmitFeedback';
import {
	string,
	object,
	number,
	shape,
	func,
	PropTypes,
	bool,
} from 'prop-types';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Card from '@mui/material/Card';
import ReplayIcon from '@mui/icons-material/Replay';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Fab from '@mui/material/Fab';
import { purple } from '@mui/material/colors';
import programTypes, {
	hasFeedBackFeature,
} from '../../../../scripts/constants';
import useOTSubmitFeedback from '../../../../data/mutationHooks/member/occupationalTherapist/useOTSubmitFeedback';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const VideoFeedbackControls = ({
	selectedRating,
	apiEnv,
	ssId,
	id,
	careTeamData,
	programType,
}) => {
	const { firstName, lastName, avatar } = careTeamData;
	const { submitFeedback, resetFeedback } =
		programType === programTypes.physicalTherapy
			? useSubmitFeedback(apiEnv, ssId)
			: useOTSubmitFeedback(apiEnv, ssId);
	const [rating, setRating] = useState(selectedRating);
	const [feedbackError, setFeedbackError] = useState(null);
	const [errorMessageOpen, setErrorMessageOpen] = useState(false);

	const handleCloseMessage = (_event, reason, setAlertMessageOpen) => {
		if (reason === 'clickaway') {
			return;
		}

		setAlertMessageOpen(false);
	};

	const handleFeedbackError = error => {
		setFeedbackError(
			`Unable to submit. Try refreshing the page or coming back later - Internal error code (${error.response.status})`
		);
		setErrorMessageOpen(true);
	};

	useEffect(() => {
		setRating(selectedRating);
	}, [selectedRating]);

	// If the user has already submitted feedback, display a view that allows them to undo if desired.
	if (rating) {
		return (
			<>
				<Snackbar
					open={errorMessageOpen}
					autoHideDuration={3000}
					onClose={(event, reason) =>
						handleCloseMessage(event, reason, setErrorMessageOpen)
					}
				>
					<Alert
						variant="filled"
						onClose={(event, reason) =>
							handleCloseMessage(event, reason, setErrorMessageOpen)
						}
						severity="error"
					>
						{feedbackError}
					</Alert>
				</Snackbar>
				<Stack
					direction="row"
					alignItems="center"
					width="100%"
					height="100%"
					spacing={1}
					p={2}
					sx={{
						backgroundColor: 'grey.50',
						borderRadius: 2,
					}}
				>
					<CheckCircleIcon color="success" />
					<Typography
						variant="body2"
						flexGrow="1"
						data-testid="difficulty-level-response"
					>
						Thanks! We&apos;ll adjust your program to make it right for you.
					</Typography>
					<Button
						variant="text"
						onClick={event => {
							// Card Element underneath this button is clickable, don't bubble click event on button press.
							event.stopPropagation();
							resetFeedback(id, {
								onError: error => handleFeedbackError(error),
								onSuccess: () => {
									setRating(null);
								},
							});
						}}
						data-testid="difficulty-level-undo-button"
					>
						Undo
					</Button>
				</Stack>
			</>
		);
	}

	// by default, show user a view with a too easy and too hard button
	return (
		<>
			<Snackbar
				open={errorMessageOpen}
				autoHideDuration={3000}
				onClose={(event, reason) =>
					handleCloseMessage(event, reason, setErrorMessageOpen)
				}
			>
				<Alert
					variant="filled"
					onClose={(event, reason) =>
						handleCloseMessage(event, reason, setErrorMessageOpen)
					}
					severity="error"
				>
					{feedbackError}
				</Alert>
			</Snackbar>
			<Stack
				width="100%"
				height="100%"
				spacing={2}
				p={2}
				sx={{
					backgroundColor: 'grey.50',
					borderRadius: 2,
				}}
				data-testid="user-rating-controls"
			>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Box>
						<Avatar
							alt={`Headshot of ${firstName} ${lastName}`}
							src={avatar}
							sx={{
								width: 32,
								height: 32,
								bgcolor: purple[50],
								color: purple[900],
							}}
						>
							{firstName[0]}
						</Avatar>
					</Box>
					<Typography variant="body2" data-testid="difficulty-level-request">
						Difficulty level not right? Let me know.
					</Typography>
				</Stack>

				<Stack
					direction="row"
					spacing={1}
					justifyContent="flex-start"
					alignItems="center"
				>
					<Chip
						label="Too Easy"
						color="secondary"
						variant="outlined"
						onClick={event => {
							// Card Element underneath this button is clickable, don't bubble click event on button press.
							event.stopPropagation();

							submitFeedback(
								{
									memberActionItemId: id,
									selectedRating: 'Too Easy',
								},
								{
									onSuccess: () => setRating('Too Easy'),
									onError: error => handleFeedbackError(error),
								}
							);
						}}
						data-testid="hep-widget-button-too-easy"
					/>
					<Chip
						label="Too Hard"
						color="secondary"
						variant="outlined"
						onClick={event => {
							// Card Element underneath this button is clickable, don't bubble click event on button press.
							event.stopPropagation();

							submitFeedback(
								{
									memberActionItemId: id,
									selectedRating: 'Too Hard',
								},
								{
									onSuccess: () => {
										setRating('Too Hard');
									},
									onError: error => handleFeedbackError(error),
								}
							);
						}}
						data-testid="hep-widget-button-too-hard"
					/>
				</Stack>
			</Stack>
		</>
	);
};

VideoFeedbackControls.propTypes = {
	/*
	 * The string that represents the feedback button the user has already clicked. Matches the ratingOptions string in API response
	 */
	selectedRating: string,
	apiEnv: string.isRequired,
	ssId: string.isRequired,
	id: string.isRequired,
	careTeamData: object.isRequired,
	programType: PropTypes.oneOf([
		programTypes.occupationalTherapy,
		programTypes.physicalTherapy,
	]),
};

VideoFeedbackControls.defaultProps = {
	selectedRating: null,
	programType: programTypes.physicalTherapy,
};

/**
 *
 * VPTRX Card
 *
 * This component is the card used inside of HEPWidget. It is extracted into its own sharable component here because EVE is currently working on finalizing Cards. Code for this card lives here for now, can be cut and copied into shared component lib at some point in the future instead.
 */
const VPTRXCard = ({
	reps,
	sets,
	frequency,
	selectedRating,
	resource,
	apiEnv,
	ssId,
	id,
	careTeamData,
	onCardClick,
	programType,
}) => {
	const { title, thumbnail } = resource;

	const hasFeedBack = hasFeedBackFeature(programType);

	const cardActions = hasFeedBack && careTeamData && (
		<CardActions sx={{ height: '100%' }}>
			<VideoFeedbackControls
				selectedRating={selectedRating}
				apiEnv={apiEnv}
				ssId={ssId}
				id={id}
				careTeamData={careTeamData}
				programType={programType}
			/>
		</CardActions>
	);

	return (
		<Card
			elevation={3}
			sx={{
				// Ensure that each card takes the full grid item area
				height: '100%',
				minWidth: '200px',
			}}
		>
			<Stack direction="column" height="100%">
				<CardActionArea
					component="div"
					disabled={onCardClick.disabled}
					onClick={() => {
						programType === programTypes.virtualMSK
							? onCardClick.handleMSKCardClick()
							: onCardClick.handleCardClick();
					}}
					data-testid={`card-${id}-action-area`}
				>
					<CardMedia
						sx={{
							height: { sm: 200, xs: 165 },
						}}
						image={thumbnail}
						title="Video demonstration of this exercise"
						alt="Video demonstration of this exercise"
					>
						<Stack
							alignItems="center"
							justifyContent="center"
							sx={{ height: '100%' }} // make sure that the stack is the same height as the cardMedia component
						>
							<Fab variant="videoButton" disabled aria-label="play video">
								<PlayArrowRoundedIcon
									sx={{ color: 'White', fontSize: '50px' }}
								/>
							</Fab>
						</Stack>
					</CardMedia>

					<CardContent>
						<Stack>
							<Stack direction="column" mt={0}>
								<Typography
									component="h2"
									variant="subtitle1"
									data-testid="exercises-card-title"
								>
									{title}
								</Typography>

								<Stack
									direction="row"
									alignItems="center"
									gap={1}
									mt={0.5}
									spacing={2}
								>
									<Stack direction="row" alignItems="center" spacing={1}>
										<ReplayIcon
											color="textSecondary"
											sx={{ fontSize: '16px' }}
										/>
										<Typography
											component="h3"
											variant="subtitle2"
											data-testid="hep-sets-reps-info"
											color="textSecondary"
										>
											{`${sets || '--'} sets x ${reps || '--'} reps`}
										</Typography>
									</Stack>
									<Stack direction="row" alignItems="center" spacing={1}>
										<CalendarTodayIcon
											color="textSecondary"
											sx={{ fontSize: '16px' }}
										/>
										<Typography
											component="h3"
											variant="subtitle2"
											data-testid="hep-frequency-info"
											color="textSecondary"
										>{`${frequency?.amount || '--'}x a ${
											!frequency || !frequency.unit || frequency.unit === 'none'
												? '--'
												: frequency.unit
										}`}</Typography>
									</Stack>
								</Stack>
							</Stack>
						</Stack>
					</CardContent>
				</CardActionArea>

				{cardActions}
			</Stack>
		</Card>
	);
};

VPTRXCard.propTypes = {
	reps: number,
	sets: number,
	frequency: shape({
		amount: number,
		unit: string,
	}),
	selectedRating: string,
	resource: object.isRequired,
	apiEnv: string.isRequired,
	ssId: string.isRequired,
	id: string.isRequired,
	careTeamData: object,
	onCardClick: shape({
		/**
		 * If `true`, the card Click action is disabled.
		 * @default false
		 */
		disabled: bool,
		handleCardClick: func.isRequired,
	}),
	programType: PropTypes.oneOf([
		programTypes.occupationalTherapy,
		programTypes.physicalTherapy,
		programTypes.virtualMSK,
	]),
};

VPTRXCard.defaultProps = {
	selectedRating: null,
	frequency: null,
	reps: null,
	sets: null,
	careTeamData: null,
	onCardClick: {
		disabled: false,
	},
	programType: programTypes.physicalTherapy,
};

export default VPTRXCard;
